"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { cx } from "@/lib/tailwind-utils";
import { SuggestionItem, suggestionPreview } from "@/types/suggestions";

// `{id}` for template variables, `{id|label}` for entity mentions.
const TOKEN_REGEX = /\{([^{}|]+)(?:\|[^{}]*)?\}/g;
type PreviewPart = {
  kind: "text";
  text: string;
} | {
  kind: "value";
  text: string;
  label: string;
};

/**
 * Splits a stored template into literal text and the preview of each variable
 * it names. A token with no matching suggestion stays as written, so a typo
 * shows up in the preview the same way it would reach a patient.
 */
export function buildPreviewParts(text: string, suggestions: SuggestionItem[]): PreviewPart[] {
  const byId = new Map(suggestions.map(s => [s.id, s]));
  const parts: PreviewPart[] = [];
  let lastIndex = 0;
  for (const match of Array.from(text.matchAll(TOKEN_REGEX))) {
    const suggestion = byId.get(match[1]);
    if (!suggestion) continue;
    const start = match.index ?? 0;
    if (start > lastIndex) {
      parts.push({
        kind: "text",
        text: text.slice(lastIndex, start)
      });
    }
    parts.push({
      kind: "value",
      text: suggestionPreview(suggestion),
      label: suggestion.label
    });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({
      kind: "text",
      text: text.slice(lastIndex)
    });
  }
  return parts;
}

/** True when the text names at least one variable a preview can fill in. */
export function hasPreviewableTokens(text: string, suggestions: SuggestionItem[]): boolean {
  if (!text || suggestions.length === 0) return false;
  const ids = new Set(suggestions.map(s => s.id));
  return Array.from(text.matchAll(TOKEN_REGEX)).some(m => ids.has(m[1]));
}

/** `{id: preview}` for every suggestion, for renderers that take a value map. */
export function previewValueMap(suggestions: SuggestionItem[]): Record<string, string> {
  return Object.fromEntries(suggestions.map(s => [s.id, suggestionPreview(s)]));
}

/**
 * A plain-text template as a recipient would read it, with each variable
 * replaced by its sample preview and tinted so the author can tell which words
 * are filled in per recipient.
 */
export function MessagePreview(t0) {
  const $ = _c(10);
  const {
    text,
    suggestions,
    className
  } = t0;
  let t1;
  if ($[0] !== suggestions || $[1] !== text) {
    t1 = buildPreviewParts(text, suggestions);
    $[0] = suggestions;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const parts = t1;
  let t2;
  if ($[3] !== className) {
    t2 = cx("whitespace-pre-wrap break-words rounded-lg border border-border bg-background-secondary px-3 py-2 text-body text-foreground", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== parts) {
    t3 = parts.map(_temp);
    $[5] = parts;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className={t2} data-testid="message-preview">{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * A "Show preview" toggle under a template editor. Renders nothing until the
 * text names a variable, so editors without any stay as they were.
 */
function _temp(part, index) {
  return part.kind === "text" ? <span key={index}>{part.text}</span> : <span key={index} title={part.label} className="rounded bg-primary/10 px-0.5 text-primary">{part.text}</span>;
}
export function MessagePreviewToggle(t0) {
  const $ = _c(19);
  const {
    text,
    suggestions,
    className
  } = t0;
  const [open, setOpen] = useState(false);
  if (!hasPreviewableTokens(text, suggestions)) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cx("space-y-1", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setOpen(_temp2);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== open) {
    t3 = open ? <FiEyeOff className="h-3.5 w-3.5" /> : <FiEye className="h-3.5 w-3.5" />;
    $[3] = open;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = open ? "Hide preview" : "Show preview";
  let t5;
  if ($[5] !== t4) {
    t5 = <span>{t4}</span>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== open || $[8] !== t3 || $[9] !== t5) {
    t6 = <button type="button" onClick={t2} className="flex items-center gap-1 text-caption-strong text-primary hover:text-primary/80" aria-expanded={open}>{t3}{t5}</button>;
    $[7] = open;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== open || $[12] !== suggestions || $[13] !== text) {
    t7 = open && <MessagePreview text={text} suggestions={suggestions} />;
    $[11] = open;
    $[12] = suggestions;
    $[13] = text;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t1 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className={t1}>{t6}{t7}</div>;
    $[15] = t1;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function _temp2(prev) {
  return !prev;
}